import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { FallingMapleLeaves } from '@/components/shared/FallingMapleLeaves';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  'ESG Advisory',
  'Carbon Market Advisory',
  'Climate Finance',
  'Sustainable Supply Chain',
  'Digital Solutions',
  'General Inquiry'
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service_interest: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service_interest: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Hero Section - Maple Theme */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 -top-20">
          <img 
            src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80"
            alt="Abstract green and brown canvas"
            className="w-full h-full object-cover"
            style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-green/8 via-transparent to-brand-brown/12" />
        </div>

        <FallingMapleLeaves />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 mb-6">
              <Send className="w-4 h-4 text-brand-green" />
              <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-wider">Get in Touch</span>
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-dark mt-2 mb-6">
              Let's Start a <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Conversation</span>
            </h1>
            <p className="font-sans text-base md:text-lg text-gray-700 leading-relaxed">
              Ready to accelerate your sustainability journey? Reach out to discuss 
              how we can help your organization achieve its ESG goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {isSubmitted ? (
                <div className="bg-brand-green/10 p-8 rounded-2xl text-center">
                  <div className="w-16 h-16 rounded-full bg-brand-green/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-brand-green" />
                  </div>
                  <h3 className="font-serif text-2xl text-brand-dark mb-4">
                    Thank You!
                  </h3>
                  <p className="font-sans text-gray-600 mb-6">
                    Your message has been received. Our team will get back to you within 
                    24-48 hours.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    className="btn-gradient text-white font-sans"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-sans text-sm font-medium text-brand-dark mb-2">
                        Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        data-testid="contact-name-input"
                        className="font-sans"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-sm font-medium text-brand-dark mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                        className="font-sans"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-sans text-sm font-medium text-brand-dark mb-2">
                        Company
                      </label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company"
                        data-testid="contact-company-input"
                        className="font-sans"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-sm font-medium text-brand-dark mb-2">
                        Phone
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="contact-phone-input"
                        className="font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-sans text-sm font-medium text-brand-dark mb-2">
                      Service Interest
                    </label>
                    <Select onValueChange={handleServiceChange} value={formData.service_interest}>
                      <SelectTrigger data-testid="contact-service-select" className="font-sans">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service} className="font-sans">
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block font-sans text-sm font-medium text-brand-dark mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your sustainability needs..."
                      data-testid="contact-message-input"
                      className="font-sans min-h-[150px]"
                      required
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-button"
                    className="w-full btn-gradient text-white font-sans py-6 text-base"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Quick Contact Card */}
              <div className="bg-brand-dark text-white p-8 rounded-2xl">
                <h3 className="font-serif text-2xl mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-brand-green" />
                    </div>
                    <div>
                      <p className="font-sans font-medium mb-1">Location</p>
                      <p className="font-sans text-white/70">Mumbai, India</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-brand-green" />
                    </div>
                    <div>
                      <p className="font-sans font-medium mb-1">Email</p>
                      <a 
                        href="mailto:info@mapleintegratedsolutions.com"
                        className="font-sans text-white/70 hover:text-white transition-colors"
                      >
                        info@mapleintegratedsolutions.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-brand-green" />
                    </div>
                    <div>
                      <p className="font-sans font-medium mb-1">Phone</p>
                      <a 
                        href="tel:+917738481009"
                        className="font-sans text-white/70 hover:text-white transition-colors"
                      >
                        +91 77384 81009
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-brand-green" />
                    </div>
                    <div>
                      <p className="font-sans font-medium mb-1">Business Hours</p>
                      <p className="font-sans text-white/70">Mon - Fri: 9:00 AM - 6:00 PM IST</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Contacts */}
              <div className="bg-brand-surface p-8 rounded-2xl">
                <h3 className="font-serif text-xl text-brand-dark mb-6">Direct Team Contacts</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-xl">
                    <p className="font-sans font-medium text-brand-dark">Tejas Patil</p>
                    <p className="font-sans text-sm text-brand-green">Co-Founder</p>
                    <a 
                      href="mailto:tejaspatil@mapleintegratedsolutions.com"
                      className="font-sans text-xs text-gray-600 hover:text-brand-green transition-colors"
                    >
                      tejaspatil@mapleintegratedsolutions.com
                    </a>
                  </div>
                  <div className="p-4 bg-white rounded-xl">
                    <p className="font-sans font-medium text-brand-dark">Vidhi Gaur</p>
                    <p className="font-sans text-sm text-brand-green">Co-Founder</p>
                    <a 
                      href="mailto:vidhigaur@mapleintegratedsolutions.com"
                      className="font-sans text-xs text-gray-600 hover:text-brand-green transition-colors"
                    >
                      vidhigaur@mapleintegratedsolutions.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
